import { formatPersonName } from './formatPersonName';

const ACTIVE_CONTACT_STORAGE_KEY = 'config-go-active-contact';
const LEGACY_SESSION_KEY = 'config-go-active-contact';

export function buildSelectedContactPayload(contact) {
  if (!contact) return null;

  const name = formatPersonName(`${contact.first_name || ''} ${contact.last_name || ''}`.trim());
  return {
    id: contact.id,
    name,
    address: contact.address || '',
    city: contact.city || '',
    postalCode: contact.postal_code || contact.postalCode || '',
    phone: contact.phone || '',
    email: contact.email || '',
    country: contact.country || '',
  };
}

/** Map quote clientDetails → selectedContact shape used on configurator-select. */
export function buildSelectedContactFromClientDetails(clientDetails) {
  if (!clientDetails) return null;

  const name = formatPersonName(clientDetails.name || '');
  const email = (clientDetails.email || '').trim();
  if (!name && !email) return null;

  return {
    id: clientDetails.contactId ?? clientDetails.id ?? null,
    name,
    address: clientDetails.address || '',
    city: clientDetails.city || '',
    postalCode: clientDetails.postalCode || '',
    phone: clientDetails.phone || '',
    email,
    country: clientDetails.country || '',
  };
}

function readStoredContactRecord() {
  try {
    const raw = localStorage.getItem(ACTIVE_CONTACT_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // Ignore parse errors
  }

  try {
    const legacy = sessionStorage.getItem(LEGACY_SESSION_KEY);
    if (!legacy) return null;
    const parsed = JSON.parse(legacy);
    if (parsed?.id || parsed?.name) {
      return { userId: null, contact: parsed };
    }
  } catch {
    // Ignore parse errors
  }

  return null;
}

export function persistActiveContactForConfigurator(contact, userId = null) {
  if (!contact) return null;

  // CRM contact (first/last name) or already-normalized selectedContact / clientDetails payload
  const payload =
    contact.first_name != null || contact.last_name != null
      ? buildSelectedContactPayload(contact)
      : buildSelectedContactFromClientDetails(contact) || buildSelectedContactPayload(contact);

  if (!payload) return null;

  try {
    localStorage.setItem(
      ACTIVE_CONTACT_STORAGE_KEY,
      JSON.stringify({ userId: userId ?? null, contact: payload })
    );
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    // Ignore storage errors (private browsing, quota, etc.)
  }

  return payload;
}

export function readActiveContactForConfigurator(userId = null) {
  const record = readStoredContactRecord();
  if (!record?.contact) return null;

  if (userId != null && record.userId != null && record.userId !== userId) {
    return null;
  }

  return record.contact;
}

export function clearActiveContactForConfigurator() {
  try {
    localStorage.removeItem(ACTIVE_CONTACT_STORAGE_KEY);
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    // Ignore storage errors
  }
}

export function resolveConfiguratorContactState(locationState, userId = null) {
  const fromState = locationState?.selectedContact;
  if (fromState?.name || fromState?.email) {
    return fromState;
  }

  return readActiveContactForConfigurator(userId);
}

/**
 * Merge CRM / session contact into quote clientDetails.
 * preferActiveContact true: incoming contact wins (explicit pick from contact profile).
 * preferActiveContact false: existing quote clientDetails win; contact fills empty fields only.
 */
export function applyActiveContactToClientDetails(
  clientDetails = {},
  selectedContact,
  { preferActiveContact = false } = {},
) {
  if (!selectedContact) return { ...clientDetails };

  const contactName = formatPersonName(selectedContact.name) || '';
  const pick = (current, incoming) => {
    const value = (current || '').trim();
    const next = (incoming || '').trim();
    if (preferActiveContact) return next || value;
    return value || next;
  };

  return {
    ...clientDetails,
    name: pick(clientDetails.name, contactName),
    address: pick(clientDetails.address, selectedContact.address),
    city: pick(clientDetails.city, selectedContact.city),
    postalCode: pick(clientDetails.postalCode, selectedContact.postalCode),
    phone: pick(clientDetails.phone, selectedContact.phone),
    email: pick(clientDetails.email, selectedContact.email),
    contactId: selectedContact.id ?? clientDetails.contactId,
  };
}

export function buildConfiguratorNavigationState(locationState, overrides = {}, userId = null) {
  const selectedContact =
    overrides.selectedContact || resolveConfiguratorContactState(locationState, userId);

  const safeState = {
    returnTo: locationState?.returnTo,
    fromContactProfile: locationState?.fromContactProfile,
    from: locationState?.from,
    preserveQuoteSession: locationState?.preserveQuoteSession,
  };

  const { selectedContact: _ignored, ...restOverrides } = overrides;

  return {
    ...safeState,
    ...restOverrides,
    ...(selectedContact ? { selectedContact } : {}),
  };
}

/**
 * When editing a saved backend quote, apply an explicitly picked CRM contact to that quote session only.
 */
export function applyExplicitContactToBackendQuoteSession(
  activeBackendQuote,
  selectedContact,
  { isEditingExisting = false } = {},
) {
  if (!isEditingExisting || !activeBackendQuote?.id || !selectedContact) {
    return null;
  }

  return {
    clientDetails: applyActiveContactToClientDetails(
      activeBackendQuote.clientDetails || {},
      selectedContact,
      { preferActiveContact: true },
    ),
    clientContactOverride: true,
  };
}
